/**
 * The words behind the ⓘ on a card: what the thing is, and how to read it.
 *
 * ⚠️ **A registry, not a permission.** An entry draws an icon beside whatever already renders
 * that id; it grants and hides nothing. Help is plain text — no markup, no HTML — so there is
 * nothing to sanitise and `dangerouslySetInnerHTML` stays banned.
 *
 * ⚠️ **Help describes the control, so it is part of the control** (D-114, D-137). A tip that
 * misstates what a figure counts is worse than no tip: the reader now believes something false
 * about their own data. Where a figure has a documented frame (raised-cohort versus resolved,
 * point-in-time versus the range), the tip says which.
 *
 * ⚠️ **Never carries a number that lives elsewhere.** A threshold or floor restated here is a
 * second copy that drifts. Say "below the minimum sample", not "below 5".
 *
 * Keyed by the same stable id the card already carries (`MetricDescriptor.id`), so a card needs
 * no extra prop and a rename of the id cannot leave a tip pointing at nothing — `help.test.ts`
 * fails on a card id with no entry.
 */

export interface HelpEntry {
  /** One sentence: what this shows or does. */
  readonly what: string;
  /** How it is calculated, what counts, what does not. The part people actually misread. */
  readonly how?: string;
  /** An internal path (`/kb/...`) for the long form. Never an external URL. */
  readonly learnMore?: string;
}

export const HELP: Readonly<Record<string, HelpEntry>> = {
  // ── Analytics: headline figures ────────────────────────────────────────────────────────
  "kpi-raised": {
    what: "Requests raised in the selected period.",
    how: "Counted by the day each request was created. Compared with the period just before it.",
  },
  "kpi-resolved": {
    what: "Requests resolved in the selected period.",
    how: "Counted by the day it was resolved, so it can include requests raised earlier. A request withdrawn by its requester counts as resolved.",
  },
  "kpi-backlog-delta": {
    what: "Whether the queue grew or shrank over the period.",
    how: "Raised minus resolved. Positive means more came in than went out.",
  },
  "kpi-first-response": {
    what: "How long people waited for the first human reply.",
    how: "Taken over requests raised in the period. Only a reply from the service desk counts, not one from the requester or an invited colleague.",
  },
  "kpi-resolution-time": {
    what: "How long requests took from raised to resolved.",
    how: "Taken over requests resolved in the period. Time a request spent waiting on the requester is not held against the desk.",
  },
  "kpi-open": {
    what: "Requests not yet resolved or closed.",
    how: "Right now. It ignores the date range above.",
  },
  "kpi-unassigned": {
    what: "Open requests with nobody working them.",
    how: "Right now. It ignores the date range above.",
  },
  "kpi-breaching": {
    what: "Open requests that have already missed their resolution target.",
    how: "Right now. It ignores the date range above. Requests with no target are not counted as late.",
  },

  // ── Analytics: quality rates ───────────────────────────────────────────────────────────
  "rate-first-response-sla": {
    what: "Of the requests raised in the period, the share answered within their first-reply target.",
    how: "Requests whose outcome is not yet known are left out of the percentage, and the count used is shown beneath it.",
  },
  "rate-resolution-sla": {
    what: "Of the requests raised in the period, the share resolved within their resolution target.",
    how: "Requests still open and not yet late are left out. A withdrawn request counts as resolved.",
  },
  "rate-reopened": {
    what: "The share of finished requests that had to be reopened.",
    how: "Taken over requests raised in the period. Only a reopen from Resolved or Closed counts. Older requests carry no history, so this figure becomes more complete over time.",
  },
  "rate-first-contact": {
    what: "The share of finished requests handled by one person, never reassigned and never reopened.",
    how: "This is about the request, not a phone call. Older requests carry no assignment history, so this becomes more complete over time.",
  },
  "rate-escalated": {
    what: "The share of requests that somebody asked to have looked at sooner.",
    how: "Counts requests, not button presses. Escalating does not change a request's priority or deadline.",
  },

  // ── Analytics: charts and tables ───────────────────────────────────────────────────────
  standouts: {
    what: "Plain observations taken straight from the figures below.",
    how: "Each line comes from a fixed rule, not a model. A rule that did not fire is simply absent. These are observations, never recommendations.",
  },
  "volume-over-time": {
    what: "How many requests came in and how many were resolved, day by day.",
    how: "Days follow your own time zone.",
  },
  "arrival-pattern": {
    what: "Which days and hours requests tend to arrive.",
    how: "Shading shows the rate per hour, not the raw count, so a day that appears twice in the range is not unfairly darker. Hatched cells were not covered by the range at all. It is about requests, never about people.",
  },
  "sla-compliance": {
    what: "How often each day's requests met their target.",
    how: "A day with no requests to judge shows as a gap, not as zero.",
  },
  "backlog-aging": {
    what: "How long the open requests have been waiting.",
    how: "Right now. It ignores the date range above.",
  },
  "sla-risk": {
    what: "Open requests closest to, or past, their deadline, grouped by priority.",
    how: "Right now. It ignores the date range above.",
  },
  "resolution-distribution": {
    what: "How resolution times were spread, band by band.",
    how: "A spread rather than an average, because an average hides the difference between mostly quick with a few very slow, and all moderately slow.",
  },
  "breakdown-site": {
    what: "The same period, cut by the site the request came from.",
    how: "Rows add up to the headline. A 'No site' row holds requests whose requester was not placed at a site. Selecting a row filters the page.",
  },
  "breakdown-category": {
    what: "What people are asking for, by category.",
    how: "Rows add up to the headline. Small categories fold into 'Other'. Categories you are not cleared to see fold into one restricted row.",
  },
  "category-movers": {
    what: "The categories whose volume changed most against the period before.",
    how: "Ranked by how many more or fewer requests, not by percentage. A rise is not automatically bad.",
  },
  "breakdown-team": {
    what: "Which team carried the work.",
    how: "Rows add up to the headline. A team with no new requests but open work still appears.",
  },
  "agent-performance": {
    what: "What each person resolved and what they are carrying now.",
    how: "Counts what they resolved in the period, which differs from the raised-in-period basis used by the rates above. Rates are marked when based on a small sample. Choose one measure to order by; there is deliberately no overall score.",
  },
  "reputation-agents": {
    what: "How requesters rated the work, per agent.",
    how: "Shown as the number of each star rating given, not just an average. It covers only requests you are allowed to see.",
  },
  "reputation-teams": {
    what: "How requesters rated the work, per team.",
    how: "It covers only requests you are allowed to see, so two administrators can see different figures and both be right.",
  },
  "top-requesters": {
    what: "Who raised the most requests in the period.",
    how: "Counts only. It is not a measure of anybody's behaviour.",
  },

  // ── Reports: request activity ──────────────────────────────────────────────────────────
  "activity-volume": {
    what: "Requests raised in the period, and how many of them have been resolved since.",
    how: "Grouped by the day each request was raised. It covers only requests you are allowed to open, so the chart and the table below it always agree.",
  },
  "activity-arrival": {
    what: "Which days and hours requests arrive.",
    how: "Shading is the rate for each occurrence of that hour, so a period with an extra Monday does not look busier. Hatched cells fall outside the period. It describes requests, never people.",
  },
  "activity-team": {
    what: "Requests by the team that holds them.",
    how: "Rows add up to the headline figure. Selecting a row narrows the whole report to it.",
  },
  "activity-site": {
    what: "Requests by the site they came from.",
    how: "Rows add up to the headline figure. 'No site' holds requests raised before a site was required, or by people not placed at a site. Selecting a row narrows the report.",
  },
  "activity-category": {
    what: "Requests by category.",
    how: "Rows add up to the headline figure. Small categories fold into 'Other'. Selecting a row narrows the report.",
  },
  "activity-status": {
    what: "Requests by their current status.",
    how: "This is where they are now, not where they were when raised.",
  },
  "activity-requesters": {
    what: "Who raised the most requests.",
    how: "Counts only, to show demand. It is not a judgement about anybody.",
  },
  "activity-assignee": {
    what: "Requests by the person holding them now.",
    how: "Includes requests nobody holds yet. It shows who has the work, not how well they did it.",
  },
  "activity-rows": {
    what: "The individual requests behind the figures above.",
    how: "Opening a request here is recorded as a view, the same as opening it from the queue.",
  },

  // ── Reports: satisfaction ──────────────────────────────────────────────────────────────
  "sat-trend": {
    what: "How satisfied requesters were, over time.",
    how: "Grouped by the day each request was raised, not the day it was rated. The most recent days are thin because ratings arrive afterwards.",
  },
  "sat-distribution": {
    what: "How many requesters gave each star rating.",
    how: "Shown as counts so a few extreme answers cannot hide behind an average.",
  },
  "sat-team": {
    what: "Satisfaction by team.",
    how: "Uses the team that held the request when it was rated, so routing it afterwards does not rewrite history.",
  },
  "sat-site": {
    what: "Satisfaction by site.",
    how: "Rows add up to the headline figure. Selecting a row narrows the report.",
  },
  "sat-category": {
    what: "Satisfaction by category.",
    how: "Rows add up to the headline figure. Selecting a row narrows the report.",
  },
  "sat-priority": {
    what: "Satisfaction by request priority.",
    how: "Priority is the one the desk set at triage.",
  },

  // ── Reports: automation and notifications ──────────────────────────────────────────────
  "auto-outcomes": {
    what: "What the standing rules made of each event, split into live and what-if.",
    how: "Live is what really happened. What-if is what a rule in what-if mode reported it would have done, with nothing changed.",
  },
  "auto-rule": {
    what: "Each rule's activity over the period.",
    how: "Includes rules since deleted, marked as such, so earlier days still add up.",
  },
  "auto-refused": {
    what: "The reasons rule actions were refused.",
    how: "This is the part to act on: the reason says what to change in the rule. Reasons name things and never quote a request.",
  },
  "auto-action": {
    what: "Which kind of action was refused.",
    how: "Use it with the reasons to see whether one kind of action is the usual cause.",
  },
  "auto-day": {
    what: "Rule activity day by day.",
    how: "Days are counted in UTC, not your own time zone.",
  },
  "notif-volume": {
    what: "How many notifications were queued each day.",
    how: "Days are counted in UTC, not your own time zone.",
  },
  "notif-event": {
    what: "Notifications by the kind of change that triggered them.",
    how: "Counts messages, not people.",
  },
  "notif-channel": {
    what: "Email compared with the in-app bell.",
    how: "Each notification is one message per channel.",
  },
  "notif-failing": {
    what: "Why email sends are failing.",
    how: "Reasons are cleaned before they are stored and ranked by how often they recur.",
  },
  "notif-recent": {
    what: "The newest notifications and what became of them.",
    how: "Newest first, with the delivery status of each.",
  },

  // ── Queue filters: keyed filter-<label, lower-cased with dashes> ───────────────────────
  "filter-status": {
    what: "Show requests in particular statuses.",
    how: "You can choose several. 'Active' means anything not resolved or closed.",
  },
  "filter-needs-attention": {
    what: "Find requests that need somebody to act.",
    how: "Each choice is a fixed rule, for example the requester replied and nobody has answered, or a request waiting on the requester has gone quiet.",
  },
  "filter-support-team": {
    what: "Only requests held by the team you choose.",
    how: "Lists the teams whose requests you can read. It narrows the queue and never widens it.",
  },
  "filter-sites": {
    what: "Only requests raised from the sites you choose.",
    how: "A site is a label on the request, not a permission. 'No site' finds requests that were never given one.",
  },
  "filter-categories": {
    what: "Only requests in the categories you choose.",
    how: "Two teams can have a category with the same name, so each is shown with its team.",
  },
  "filter-rating": {
    what: "Filter by the stars the requester gave.",
    how: "'Not rated' finds requests the survey never reached. The survey is asked once, after a request is resolved.",
  },
  "filter-raised-by": {
    what: "Whether a person or a connected application raised the request.",
  },
  "filter-requester": {
    what: "Only requests raised for a particular person.",
    how: "It narrows what you can already see. It does not show you anyone else's requests.",
  },
  "filter-assignee": {
    what: "Only requests held by a particular agent.",
    how: "It narrows what you can already see.",
  },
  "filter-priority": {
    what: "Show requests at or above a priority.",
    how: "This is the priority the desk set when it triaged the request.",
  },
  "filter-attachments": {
    what: "Whether a request has a file attached.",
    how: "'Nothing attached' finds requests that arrived without a file. A file still being scanned counts as attached.",
  },
  "filter-raised-from": {
    what: "Requests raised on or after this day.",
  },
  "filter-raised-to": {
    what: "Requests raised up to and including this day.",
  },

  // ── Request form, profile, follow-ups, automation, more reports ────────────────────────
  "ticket-conversation": {
    what: "How much back-and-forth there has been between the requester and the desk.",
    how: "Counted from public replies only, not views, internal notes or time logged. Shown to the service desk only.",
  },
  "new-requester": {
    what: "Who the request is for.",
    how: "Raising it for somebody else makes the request theirs: they can see it and are notified. You are still recorded as the person who raised it.",
  },
  "new-team": {
    what: "Which team should handle this.",
    how: "Pick the closest one. The desk can move it if it lands in the wrong place.",
  },
  "new-category": {
    what: "The kind of problem this is.",
    how: "The category decides which team handles it and how quickly the desk commits to reply. Pick the closest; the desk can correct it.",
  },
  "new-review": {
    what: "A last look before you send.",
    how: "Nothing is sent until you submit.",
  },
  "profile-picture": {
    what: "The picture shown beside your name on requests and replies.",
    how: "Only common picture formats are accepted.",
  },
  "profile-access": {
    what: "What you can do in this application.",
    how: "Read-only. Access is granted inside this application by an administrator, not from your Microsoft account groups.",
  },
  "profile-appearance": {
    what: "Light or dark display.",
    how: "It changes how the app looks for you and nothing about your access.",
  },
  "todos-overdue": {
    what: "Follow-ups whose date has passed.",
    how: "Your follow-ups are private to you. They are not assigned to anyone else.",
  },
  "todos-today": {
    what: "Follow-ups due today.",
    how: "Your follow-ups are private to you.",
  },
  "todos-coming-up": {
    what: "Follow-ups with a date still ahead.",
    how: "Your follow-ups are private to you.",
  },
  "todos-no-date": {
    what: "Follow-ups with no due date.",
    how: "They never become overdue and you are never reminded about them.",
  },
  "automation-rule": {
    what: "A standing rule: when something happens, if conditions match, do these actions.",
    how: "A new rule starts Off and in What-if, where it reports what it would have done without changing anything. Switch it to Live when you are happy with that.",
  },
  "sec-hours": {
    what: "When records were read, across every account together.",
    how: "Folded by hour of the day. It has no person axis, so it cannot show when one individual is at their desk.",
  },
  "sec-standouts": {
    what: "Which access rules fired, and by whom.",
    how: "Every line is a fixed rule you can check, and one that did not fire says zero. These are observations, not a risk score.",
  },
  "sec-accounts": {
    what: "Accounts that triggered at least one rule.",
    how: "Open an account to see the evidence behind its flags. Opening it is recorded in the audit log.",
  },
  "usage-admins": {
    what: "Administrator accounts and whether they are in use.",
    how: "Administrators who never signed in are listed with the dormant ones, because standing access with nobody behind it is a risk worth reviewing.",
  },
  "usage-monthly": {
    what: "How many accounts used the service desk each month.",
    how: "Counts accounts, not requests or sessions. Months before measuring began are blank rather than zero, and the first and latest months are partial.",
  },
  "usage-site": {
    what: "How many of each site's accounts have used the service desk.",
  },
  "deflect-assistant": {
    what: "How the chat assistant did at resolving questions without a request.",
    how: "Conversations are kept for a limited time and then removed.",
  },
  "deflect-guidance": {
    what: "Guidance shown on the new-request form before somebody submits.",
    how: "Lifetime totals for each rule, not limited to the period.",
  },
  "deflect-kb": {
    what: "The most-viewed knowledge-base articles in the period.",
    how: "A high view count with a low helpful share suggests the article needs rewriting.",
  },

  "people-directory": {
    what: "Everyone with an account, and people in the company directory who have not signed in yet.",
    how: "Names, job titles, managers and sites come from the company directory. What somebody can do here is set inside this application, never taken from the directory.",
  },

  // ── My activity ────────────────────────────────────────────────────────────────────────
  "myact-raised": {
    what: "The requests you raised in this period and what became of them.",
    how: "Counted by the day each was raised, so it describes what you asked for, not what was finished.",
  },
  "myact-open": {
    what: "Your open requests, grouped by where they stand.",
    how: "Uses the same wording as on the request itself. Selecting a row opens those requests.",
  },
  "myact-asks": {
    what: "The kinds of thing you ask the service desk for.",
    how: "Small categories are grouped under 'Other'. The rows add up to the total.",
  },
  "myact-took": {
    what: "How long your finished requests took.",
    how: "Shown as bands and a middle value, not an average. Requests not yet finished are left out and counted separately.",
  },
  "mywork-finished": {
    what: "Requests assigned to you that you finished in this period.",
    how: "Counted by when they were finished, which is a different basis from 'Your own requests' below. Never compared with anyone else.",
  },
  "mywork-how": {
    what: "How the requests you finished turned out.",
    how: "Rates based on only a few requests are marked as a small sample rather than hidden. There are no targets or grades here.",
  },
  "mywork-took": {
    what: "How long the requests you finished took.",
    how: "Shown as bands and a middle value, not an average.",
  },
  "mywork-time": {
    what: "Time recorded by your work timer.",
    how: "It is a floor on the effort, not a full measure of it. If no timer ran it says 'not logged', which is not the same as zero.",
  },
  "mywork-said": {
    what: "Ratings requesters gave on requests you held when they were rated.",
    how: "Shown as a count of each star rather than one average, and with no comparison to colleagues. It is not limited to the period.",
  },
  "mywork-list": {
    what: "The requests behind the figures above.",
    how: "Opening one is recorded as a view, the same as opening it from the queue.",
  },
  "mywork-reach": {
    what: "Which teams and kinds of request your access covers.",
    how: "This describes your access, not the data, so two people can see different figures and both be right.",
  },

  // ── Settings: one per section, keyed settings-<section id> ─────────────────────────────
  "settings-features-ticketing": {
    what: "Switches for how requests behave: attachments, reopening, holds and exports.",
    how: "A switch applies to everybody at once. Turning one off hides the feature without deleting anything already recorded.",
  },
  "settings-features-assistance": {
    what: "Switches for the AI assistant and suggestions.",
    how: "These stay off and fail closed until the required Microsoft approval is in place, so a switch may be locked.",
  },
  "settings-features-notifications": {
    what: "Switches for each way the system tells people things: email, the in-app bell and desktop alerts.",
    how: "Turning a channel off stops it for everyone. People can silence individual kinds on their own profile, but a few important notices can never be silenced.",
  },
  "settings-features-directory": {
    what: "Switches for how the system keeps its list of people in step with Entra.",
    how: "Entra decides who exists and what their directory details are; access inside this application is still granted here.",
  },
  "settings-features-access": {
    what: "Switches for emergency access and sign-in help.",
    how: "Changes here affect how people get in, so check with the security owner before switching one.",
  },
  "settings-integrations": {
    what: "The outside services this application uses, such as email delivery and phone.",
    how: "Changing a connection can stop messages being sent. Only one email provider can be active at a time.",
  },
  "settings-connected-apps": {
    what: "Other applications allowed to raise requests here over the API.",
    how: "Each app can only create requests, in the categories you allow, and cannot read anything back. Keys are shown once when issued and expire.",
  },
  "settings-sla": {
    what: "How quickly each priority should be answered and resolved, and when the desk is open.",
    how: "Changing a target does not move the deadlines of requests already open. Teams and categories can set their own targets, which take precedence over these defaults.",
  },
  "settings-saved-replies": {
    what: "Ready-made replies agents can insert into a request and edit before sending.",
    how: "Organisation-wide replies are managed here. Agents also keep their own private ones on their profile.",
  },
  "settings-sign-out": {
    what: "Where people land after they sign out.",
    how: "Signing out ends the session here; it does not sign them out of other Microsoft apps.",
  },
  "settings-external-domains": {
    what: "Outside organisations whose people can be involved in requests.",
    how: "Notifications to people at these domains never contain request content, only that something needs their attention.",
  },
  "settings-notification-templates": {
    what: "The wording and layout of each email the system sends.",
    how: "Placeholders fill in only what the recipient is already allowed to see. Each person's copy is built separately.",
  },
  "settings-attachment-formats": {
    what: "Which kinds of file people may attach to a request.",
    how: "Files are identified by what is inside them, never by their name, so you cannot allow a type by typing an extension. Archives and saved emails are checked entry by entry.",
  },
  "settings-vip-titles": {
    what: "Job titles that mark someone as a VIP.",
    how: "A VIP flag affects routing and how a request is presented. It never gives anyone extra access.",
  },
  "settings-ai-transcripts": {
    what: "Conversations people had with the chat assistant.",
    how: "Read-only, for checking the assistant is not being misused.",
  },
  "settings-security": {
    what: "What the application enforces, and what it does not yet.",
    how: "Read-only. It reports the current state; it cannot change it.",
  },

  // ── Admin: tabs and team panels ────────────────────────────────────────────────────────
  "admin-roles": {
    what: "Bundles of permissions that can be given to people, globally or on a team.",
    how: "You can only give a permission, scope or clearance that you hold yourself. Editing a role changes access for everyone who holds it.",
  },
  "admin-permissions": {
    what: "Every permission in the system and which roles hold it.",
    how: "Read-only reference. Some permissions are reserved for system roles and cannot be given to custom ones.",
  },
  "admin-teams": {
    what: "Support teams, the categories they own, their members, targets, hours and holidays.",
    how: "Every team serves all sites; a site is only a label on a request. Each category belongs to exactly one team.",
  },
  "admin-sites": {
    what: "The locations requests can come from, and the rule that places each person at one.",
    how: "People are placed by one directory field you choose. If the rule does not match, they are placed at no site rather than guessed. Two sites cannot share a rule.",
  },
  "admin-audit": {
    what: "The permanent record of who did what.",
    how: "Entries cannot be edited or removed. Searching the log is itself recorded.",
  },
  "team-hours": {
    what: "When this team's desk is open, in the team's own time zone.",
    how: "A complete week applies as a whole or not at all. With none set, the organisation's week is used and measured at each request's own site.",
  },
  "team-holidays": {
    what: "Days this team's desk is closed.",
    how: "Holidays are rules such as 'fourth Thursday in November', so they never run out. A team's own list replaces the organisation's entirely rather than adding to it.",
  },
  "team-oncall": {
    what: "The number to call out of hours for urgent problems with this team.",
    how: "Keep it pointing at a number that someone will answer.",
  },
  "team-links": {
    what: "Let two teams work each other's requests as an agent would.",
    how: "A link widens reach only: it gives no clearance for sensitive categories and does not add anyone to the other team's notifications. Either team's administrator can end it.",
  },

  // ── Request screen: side panels ────────────────────────────────────────────────────────
  "ticket-sla": {
    what: "When the service desk has committed to reply to and to resolve this request.",
    how: "The clock only runs during the desk's working hours, and it pauses while the request is waiting on the requester or on hold. When a pause ends, the deadline moves back by the working time spent waiting.",
  },
  "ticket-actions": {
    what: "Move the request to its next step.",
    how: "Only the steps you are allowed to take are offered. Putting a request on hold asks for an end date and a reason; the requester can see that reason.",
  },
  "ticket-details": {
    what: "The facts about this request: who it is for, where it came from, who has it and how it is classified.",
    how: "Where you are allowed to change something, an edit control appears beside it. Changes are recorded in the request history.",
  },
  "ticket-source": {
    what: "This request was filed automatically by another application, not typed by a person.",
    how: "The name shown is the connected application, which cannot be set by whoever sent the request.",
  },
  "ticket-timer": {
    what: "How long you have spent working this request.",
    how: "It starts by itself when you work the request and stops after a period of inactivity, recording up to your last action rather than the moment it noticed. You can also start it yourself, for example for a phone call. It is a floor on effort, not a full measure of it.",
  },
  "ticket-followups": {
    what: "Your own private reminders to come back to this request.",
    how: "Only you can see them. They are not assigned to anyone and do not appear on the request. You are reminded in the app and by email when one comes due, and the email does not include what you wrote.",
  },
  "ticket-escalate": {
    what: "Ask for this request to be looked at sooner, and see whether anyone already has.",
    how: "Chasing does not change the priority, status, owner or deadline. It notifies whoever is working the request. You can chase again after a few hours.",
  },
};

/** The entry for an id, or `undefined`. A missing id draws no icon rather than an empty one. */
export function helpFor(id: string): HelpEntry | undefined {
  return Object.prototype.hasOwnProperty.call(HELP, id) ? HELP[id] : undefined;
}

/**
 * `learnMore` must stay on this application: a help entry is not a place to send people
 * somewhere else, and a registry editable by a typo should not be able to.
 */
export function isInternalPath(path: string): boolean {
  return /^\/(?!\/)[A-Za-z0-9/_\-.#?=&]*$/.test(path);
}
